"use client";

import { useEffect, useState } from "react";
import { useUniversityLinkVisible } from "@/hooks/useUniversityLink";
import { getUniversityLinkQuestions } from "@/lib/universityLink/api";
import type { UniversityLinkQuestion } from "@/lib/universityLink/types";

export interface UseUniversityQuestionsResult {
  /** 大学が登録した質問（新しい順）。名簿に一致しない・取得前・取得失敗のときは空 */
  questions: UniversityLinkQuestion[];
  /** 名簿一致が分かってから結果が届くまで true */
  loading: boolean;
  failed: boolean;
}

const EMPTY: UniversityLinkQuestion[] = [];

type Fetched = { questions: UniversityLinkQuestion[]; failed: boolean };

// 質問選択画面が大学の質問を合流させるためのフック（#1861）。
// 名簿に一致する学生（大学連携がナビに出る状態）のときだけ取得し、招待のないユーザーと
// ゲストには要求を出さない。取得に失敗しても大学質問を空にして通常の一覧はそのまま使えるようにする。
export function useUniversityQuestions(): UseUniversityQuestionsResult {
  const visible = useUniversityLinkVisible();
  const [fetched, setFetched] = useState<Fetched | null>(null);

  useEffect(() => {
    if (!visible) return;
    let cancelled = false;
    getUniversityLinkQuestions()
      .then((response) => {
        if (cancelled) return;
        setFetched({ questions: response.eligible ? response.questions : EMPTY, failed: false });
      })
      .catch(() => {
        if (cancelled) return;
        setFetched({ questions: EMPTY, failed: true });
      });
    return () => {
      cancelled = true;
    };
  }, [visible]);

  if (!visible) return { questions: EMPTY, loading: false, failed: false };
  if (!fetched) return { questions: EMPTY, loading: true, failed: false };
  return { questions: fetched.questions, loading: false, failed: fetched.failed };
}
